<template>
	<div class="db2-trend">
		<div class="db2-trend-head">
			<div class="db2-trend-title">
				<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
				<span>{{ card.name }}</span>
			</div>
		</div>
		<div class="db2-trend-legend">
			<span class="db2-trend-leg" v-for="(item, i) in legendItems" :key="i">
				<span class="db2-trend-leg-swatch" :style="{ background: item.color }"></span>
				{{ item.label }}
			</span>
		</div>
		<HaqEChart class="db2-trend-chart" :option="option" height="100%" />
	</div>
</template>

<script>
import HaqEChart from '../HaqEChart.vue';
import { mockPointData, mockAxisLabels } from '../mockPointData';
import { echarts, chartTheme, baseChartOption } from '../chartTheme';

/** Axis tick / name size. Set on each axis so baseChartOption 15 / inherit cannot win. */
const AXIS_FONT = 11;

/** Tick-to-axis gap. Name padding matches this so the unit sits on the number column. */
const AXIS_LABEL_MARGIN = 8;

/** High-contrast series colors: blue, green, red. */
const TREND_COLORS = ['#5b8def', '#4ade80', '#e45b73'];

/**
 * Hex color with an alpha channel for the fade under each line.
 * @param {string} color
 * @param {number} alpha
 * @returns {string}
 */
function withAlpha(color, alpha) {
	const hex = String(color || '').replace('#', '');
	if (/^[0-9a-fA-F]{6}$/.test(hex)) {
		const n = parseInt(hex, 16);
		return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
	}
	return color || `rgba(91,141,239,${alpha})`;
}

/**
 * Fade-out fill from the line down to the plot floor.
 * Mid stop makes most of the fade happen in the upper half (not a linear ramp).
 * @param {string} color
 * @returns {object}
 */
function trendAreaFill(color) {
	return {
		origin: 'start',
		color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
			{ offset: 0, color: withAlpha(color, 0.31) },
			{ offset: 0.4, color: withAlpha(color, 0.08) },
			{ offset: 1, color: withAlpha(color, 0) }
		])
	};
}

/**
 * Fixed scales for the three zone-performance series (temp, humidity, CO2).
 */
const TREND_AXES = [
	{ min: 60, max: 80, interval: 5, unit: '°F', name: '°F', suffix: '', color: TREND_COLORS[0], amp: 5, legend: 'Temperature (°F)' },
	{ min: 0, max: 60, interval: 15, unit: '%RH', name: '%RH', suffix: '', color: TREND_COLORS[1], amp: 12, legend: 'Humidity (%RH)' },
	{ min: 0, max: 1200, interval: 300, unit: 'ppm', name: 'ppm', suffix: '', color: TREND_COLORS[2], amp: 280, legend: 'CO2 (ppm)' }
];

/**
 * Unit title over the tick column. ECharts `name` anchors on the axis line, not the
 * label box, so padding shifts the name by AXIS_LABEL_MARGIN onto the numbers.
 * Left: pad-right moves a right-aligned name left. Right: pad-left moves a left-aligned name right.
 * @param {{ color: string }} axis
 * @param {number} index
 * @param {{ fontSize: number, fontWeight: number }} labelStyle
 * @returns {object}
 */
function trendNameTextStyle(axis, index, labelStyle) {
	const isLeft = index === 0;
	return {
		color: axis.color,
		...labelStyle,
		align: isLeft ? 'right' : 'left',
		padding: isLeft ? [0, AXIS_LABEL_MARGIN, 0, 0] : [0, 0, 0, AXIS_LABEL_MARGIN]
	};
}

/**
 * Axis meta for a series index (extras share the last / CO2 axis).
 * @param {number} index
 * @returns {typeof TREND_AXES[0]}
 */
function trendAxis(index) {
	return TREND_AXES[Math.min(index, TREND_AXES.length - 1)];
}

/**
 * Legend caption with unit, matching the design.
 * @param {{ label?: string }} series
 * @param {number} index
 * @returns {string}
 */
function trendLegendLabel(series, index) {
	const axis = trendAxis(index);
	const name = (series && series.label) || axis.legend;
	if (name.indexOf('(') >= 0) return name;
	return name + ' (' + axis.unit + ')';
}

/**
 * Multi-series line chart with three Y-axes (temperature, humidity, CO2).
 * Time range comes from the dashboard `timePeriod` prop, not a local dropdown.
 */
export default {
	name: 'TrendCard',
	components: { HaqEChart },
	props: {
		card: { type: Object, required: true },
		isEditMode: { type: Boolean, default: false },
		timePeriod: { type: String, default: 'week' },
		refreshKey: { type: Number, default: 0 }
	},
	computed: {
		/**
		 * Dashboard header time range.
		 * @returns {string}
		 */
		range() {
			return this.timePeriod || 'week';
		},
		/**
		 * Series config from the card, or the three default traces.
		 * @returns {Array<{ label: string, color: string, base?: number, pointId?: string, amp?: number }>}
		 */
		seriesList() {
			const cfg = this.card.config || {};
			if (cfg.series && cfg.series.length) {
				return cfg.series.map((s, i) => ({
					...s,
					color: TREND_COLORS[i] || s.color
				}));
			}
			return [
				{ label: 'Temperature', color: TREND_COLORS[0], base: 72 },
				{ label: 'Humidity', color: TREND_COLORS[1], base: 45 },
				{ label: 'CO2', color: TREND_COLORS[2], base: 620 }
			];
		},
		/**
		 * Centered HTML legend items (pill + muted label).
		 * @returns {Array<{ label: string, color: string }>}
		 */
		legendItems() {
			return this.seriesList.map((s, i) => ({
				label: trendLegendLabel(s, i),
				color: s.color || trendAxis(i).color || TREND_COLORS[i % TREND_COLORS.length]
			}));
		},
		/**
		 * ECharts option.
		 * @returns {object}
		 */
		option() {
			const t = chartTheme();
			const seriesCfg = this.seriesList;
			const first = mockPointData((seriesCfg[0] && seriesCfg[0].pointId) || this.card.uniqueId + '-a', this.range, {
				base: seriesCfg[0] && seriesCfg[0].base,
				refreshKey: this.refreshKey
			});
			const count = this.range === 'week' ? Math.min(7, first.series.length) : first.series.length;
			const labels = mockAxisLabels(this.range, count);
			const colors = TREND_COLORS;
			const labelStyle = { fontSize: AXIS_FONT, fontWeight: 400 };
			return {
				...baseChartOption(t),
				// Drop inherit so canvas can honor AXIS_FONT (11).
				textStyle: { fontSize: AXIS_FONT },
				legend: { show: false },
				grid: { left: 58, right: 100, top: 30, bottom: 34, containLabel: false },
				xAxis: {
					type: 'category',
					data: labels,
					boundaryGap: false,
					axisTick: { show: false },
					axisLine: { show: false },
					splitLine: { show: false },
					textStyle: { fontSize: AXIS_FONT },
					nameTextStyle: { fontSize: AXIS_FONT },
					axisLabel: { color: t.muted, ...labelStyle, fontSize: AXIS_FONT }
				},
				yAxis: TREND_AXES.map((axis, i) => ({
					type: 'value',
					position: i === 0 ? 'left' : 'right',
					offset: i === 2 ? 48 : 0,
					min: axis.min,
					max: axis.max,
					interval: axis.interval,
					name: axis.name,
					nameLocation: 'end',
					nameGap: 8,
					nameRotate: 0,
					textStyle: { fontSize: AXIS_FONT },
					nameTextStyle: { ...trendNameTextStyle(axis, i, labelStyle), fontSize: AXIS_FONT },
					splitLine: i === 0
						? { show: true, lineStyle: { color: t.line } }
						: { show: false },
					axisTick: { show: false },
					axisLine: { show: false },
					axisLabel: {
						color: axis.color,
						...labelStyle,
						fontSize: AXIS_FONT,
						align: i === 0 ? 'right' : 'left',
						margin: AXIS_LABEL_MARGIN,
						formatter: axis.suffix ? '{value}' + axis.suffix : '{value}'
					}
				})),
				series: seriesCfg.map((s, i) => {
					const axis = trendAxis(i);
					const d = mockPointData(s.pointId || this.card.uniqueId + '-' + i, this.range, {
						base: s.base,
						amp: s.amp != null ? s.amp : axis.amp,
						refreshKey: this.refreshKey
					});
					const color = s.color || colors[i % colors.length];
					return {
						name: s.label || ('Series ' + (i + 1)),
						type: 'line',
						smooth: true,
						showSymbol: false,
						yAxisIndex: Math.min(i, TREND_AXES.length - 1),
						data: d.series.slice(-count),
						itemStyle: { color: color },
						lineStyle: { width: 2, color: color },
						areaStyle: trendAreaFill(color)
					};
				})
			};
		}
	},
	methods: {
		/**
		 * Start a canvas move from the in-card title handle.
		 * @param {MouseEvent} e
		 */
		onDragStart(e) {
			if (!this.isEditMode) return;
			this.$emit('drag-start', e);
		}
	}
};
</script>
